"use client";

import { User } from "lucide-react";
import { useState } from "react";

import { cn } from "@/lib/utils";

import { VS_FONT_FEATURES } from "./constants";
import type { Speaker } from "./data";
import { SPEAKERS } from "./data";

interface VsSpeakersProps {
  className?: string;
  /** Anchor id for the chapter nav / in-page links. Defaults to "speakers". */
  id?: string;
  /** Heading text. Defaults to "Speakers." */
  title?: string;
  /** Optional subtext rendered beneath the heading. */
  subtitle?: string;
  /** Speaker list to render. Defaults to the shared `SPEAKERS` export. */
  speakers?: readonly Speaker[];
  /** Smaller cards, 6 to a row on desktop instead of 4. */
  compact?: boolean;
}

/** Speakers shown before the "See more" toggle is expanded (default: two rows of four; compact: two rows of six). */
const INITIAL_COUNT = 8;
const INITIAL_COUNT_COMPACT = 12;

/**
 * Responsive card grid of speakers. Shows the first {@link INITIAL_COUNT}
 * (2 rows of 4 on desktop, or 2 rows of 6 in `compact` mode) with a
 * "See more" toggle that reveals the rest. Each card is a headshot with the
 * name, brand, and a small "London"/"New York" location badge beneath;
 * placeholder entries (no `src`) render a neutral avatar.
 */
export function VsSpeakers({
  className,
  id = "speakers",
  title = "Speakers.",
  subtitle,
  speakers = SPEAKERS,
  compact = false,
}: VsSpeakersProps) {
  const [expanded, setExpanded] = useState(false);

  const shown = speakers.filter((speaker) => !speaker.hidden);
  const initialCount = compact ? INITIAL_COUNT_COMPACT : INITIAL_COUNT;
  const visible = expanded ? shown : shown.slice(0, initialCount);
  const remaining = shown.length - initialCount;

  return (
    <div
      id={id}
      style={{ fontFeatureSettings: VS_FONT_FEATURES }}
      className={cn("scroll-mt-[120px] py-[72px] max-md:py-12", className)}
    >
      <h2 className="mb-12 text-[40px] leading-[1.2] font-normal tracking-[-0.04em] text-white max-md:mb-8 max-md:text-[32px]">
        {title}
      </h2>
      {subtitle ? (
        <p className="-mt-8 mb-12 max-w-[640px] text-[16px] leading-[1.4] text-white/60 max-md:-mt-6 max-md:mb-8 max-md:text-[14px]">
          {subtitle}
        </p>
      ) : null}

      <ul
        className={cn(
          "grid gap-4",
          compact
            ? "grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6"
            : "grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
        )}
      >
        {visible.map((speaker, i) => (
          <li
            key={`${speaker.name}-${i}`}
            className="overflow-hidden rounded-2xl bg-[#171717]"
          >
            <div className="relative aspect-[4/5] w-full bg-[#1c1c1c]">
              {speaker.src ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={speaker.src}
                  alt={speaker.name}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover object-top"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <User
                    size={compact ? 22 : 32}
                    strokeWidth={1.5}
                    className="text-white/15"
                  />
                </div>
              )}
              <span
                className={cn(
                  "absolute top-2 left-2 rounded-full bg-black/60 font-medium text-white uppercase backdrop-blur-sm",
                  compact
                    ? "px-1.5 py-0.5 text-[9px] tracking-[0.04em]"
                    : "px-2 py-1 text-[10px] tracking-[0.05em]"
                )}
              >
                {speaker.location}
              </span>
            </div>
            <div className={compact ? "p-2.5" : "p-4 max-md:p-3"}>
              <p
                className={cn(
                  "font-semibold tracking-[-0.015em] text-white",
                  compact ? "text-[13px]" : "text-[16px] max-md:text-[14px]"
                )}
              >
                {speaker.name}
              </p>
              {speaker.brand ? (
                <p
                  className={cn(
                    "mt-0.5 tracking-[-0.0125em] text-white/55",
                    compact ? "text-[12px]" : "text-[14px] max-md:text-[13px]"
                  )}
                >
                  {speaker.brand}
                </p>
              ) : null}
            </div>
          </li>
        ))}
      </ul>

      {remaining > 0 ? (
        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className={cn(
              "inline-flex h-10 items-center rounded-full border border-[#313335] px-5 text-[14px] font-medium text-white",
              "transition-colors hover:bg-white/5 motion-reduce:transition-none",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d0d0d]"
            )}
          >
            {expanded ? "See fewer" : `See more (${remaining})`}
          </button>
        </div>
      ) : null}
    </div>
  );
}
